import { useEffect, useRef, type ReactNode } from 'react';
import '../../styles/admin.css';

interface AdminShellProps {
  sidebar: ReactNode;
  chat: ReactNode;
  profile: ReactNode;
  /** 移动端 ≤760px 时显示列表还是聊天；桌面端忽略 */
  mobileView?: 'list' | 'chat';
  /** 移动端画像面板全屏覆盖开关；桌面端忽略 */
  profileOpen?: boolean;
}

export function AdminShell({ sidebar, chat, profile, mobileView = 'chat', profileOpen = false }: AdminShellProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const shell = shellRef.current;
    const bar = scrollbarRef.current;
    if (!shell || !bar) return undefined;
    const thumb = bar.firstElementChild as HTMLElement | null;
    const syncWidth = () => {
      if (!thumb) return;
      const ratio = Math.min(1, bar.clientWidth / shell.scrollWidth);
      thumb.style.width = `${Math.max(48, bar.clientWidth * ratio)}px`;
      thumb.style.left = `${shell.scrollWidth > shell.clientWidth ? (shell.scrollLeft / (shell.scrollWidth - shell.clientWidth)) * (bar.clientWidth - thumb.offsetWidth) : 0}px`;
    };
    const syncBar = () => { if (bar.scrollLeft !== shell.scrollLeft) bar.scrollLeft = shell.scrollLeft; };
    const syncShell = () => { if (shell.scrollLeft !== bar.scrollLeft) shell.scrollLeft = bar.scrollLeft; syncWidth(); };
    const onPointerDown = (event: PointerEvent) => {
      if (!thumb?.contains(event.target as Node)) return;
      const startX = event.clientX; const startScroll = shell.scrollLeft;
      const move = (moveEvent: PointerEvent) => { const track = Math.max(1, bar.clientWidth - thumb.offsetWidth); shell.scrollLeft = startScroll + (moveEvent.clientX - startX) / track * (shell.scrollWidth - shell.clientWidth); syncWidth(); };
      const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up, { once: true });
    };
    syncWidth();
    shell.addEventListener('scroll', syncBar, { passive: true });
    bar.addEventListener('scroll', syncShell, { passive: true });
    bar.addEventListener('pointerdown', onPointerDown);
    const observer = new ResizeObserver(syncWidth);
    observer.observe(shell);
    return () => { shell.removeEventListener('scroll', syncBar); bar.removeEventListener('scroll', syncShell); bar.removeEventListener('pointerdown', onPointerDown); observer.disconnect(); };
  }, []);
  return (
    <div className="admin-shell-wrap">
    <div ref={shellRef} className={`admin-shell mobile-${mobileView}`}>
      <aside className="sidebar">{sidebar}</aside>
      <main className="chat-panel">{chat}</main>
      <aside className={`profile-panel ${profileOpen ? 'open' : ''}`}>{profile}</aside>
    </div>
    <div ref={scrollbarRef} className="admin-shell-scrollbar" aria-label="横向滚动"><div className="admin-shell-scrollbar-thumb" /></div>
    </div>
  );
}
